import { Children, isValidElement, useEffect, useRef, useState } from 'react';
import { Box } from '../';
import { Group } from './Group';

function getColumnEndIndexes(length, columns) {
  const counts = new Array(columns).fill(0);

  for (let i = 0; i < length; i++) {
    counts[i % columns]++;
  }

  const endIndexes = [];
  let sum = 0;
  for (let count of counts) {
    sum += count;
    endIndexes.push(sum);
  }

  return endIndexes;
}

export const Masonry = ({ children }) => {
  const containerRef = useRef(null);
  const [orderedChildren, setOrderedChildren] = useState(null);

  const order = async () => {
    if (!containerRef.current) return;
    const reactChildren = Children.toArray(children);
    const groups = Array.from(containerRef?.current?.children);

    const list = groups.map((element, idx) => ({
      key: reactChildren[idx]?.key,
      height: element.offsetHeight,
    }));

    const first = list[0];
    const last = list[list.length - 1];
    const items = list.slice(1, -1);

    let permutations = [list];
    for (let i = 0; i < items.length - 1; i++) {
      let newList = [...items];
      const item1 = items[i];
      const item2 = items[i + 1];
      newList[i] = item2;
      newList[i + 1] = item1;
      permutations.push([first, ...newList, last]);
    }

    const indices = getColumnEndIndexes(list.length, 2);
    let bestHeight = Infinity;
    let bestPermutation = null;
    for (let idx = 0; idx < permutations.length; idx++) {
      const p = permutations[idx];
      let maxHeight = -Infinity;
      let lastI = 0;
      for (const i of indices) {
        const column = p.slice(lastI, i);
        const columnHeight = column.reduce((sum, item) => sum + item.height, 0);
        if (columnHeight > maxHeight) {
          maxHeight = columnHeight;
        }
        lastI = i;
      }
      if (maxHeight < bestHeight) {
        bestHeight = maxHeight;
        bestPermutation = p;
      }
    }

    let next = [];
    for (const perm of bestPermutation) {
      const child = reactChildren.find((child) => {
        if (isValidElement(child)) {
          const identifier = child.key;
          return identifier === perm.key;
        }
        return false;
      });
      next.push(child);
    }

    setOrderedChildren(next);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        order();
      });
    });
  }, [children]);

  return (
    <>
      <Box
        css={`
          column-count: 2;
          column-gap: 16px;
        `}
      >
        {orderedChildren}
      </Box>
      <Box
        css={`
          position: fixed;
          top: -9999px;
          left: -9999px;
          width: 1000px;
          visibility: hidden;
          pointer-events: none;
          z-index: -1;
        `}
      >
        <Box
          ref={containerRef}
          css={`
            column-count: 2;
            column-gap: 16px;
          `}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};

Masonry.Group = Group;
